/**
 * Me → «Мои карты»: the own chart («Я») first, then saved charts of other people.
 * Loading / error / list follow Waitlist.tsx. Each card is its own component so it owns its
 * `useUpdateChart(id)` hook for the «Повторить» button.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MeSubpageShell } from '../../../components/chrome/MeSubpageShell';
import { Big3Strip } from '../../../components/me/Big3Strip';
import { EmptyState } from '../../../components/me/EmptyState';
import { useCharts, useUpdateChart, type ChartItem } from '../../../hooks/useCharts';
import { formatBirthDate } from '../../../lib/format-date';
import { relationDisplayKey } from '../../../lib/relation';
import styles from './Charts.module.css';

/** The server names the own chart «Я» when the profile has no first name. */
const NO_NAME = 'Я';

interface ChartCardProps {
  chart: ChartItem;
  stalled: boolean;
  onOpen: () => void;
  onRestartPolling: (id: string) => void;
}

function ChartCard({ chart, stalled, onOpen, onRestartPolling }: ChartCardProps): JSX.Element {
  const { t, i18n } = useTranslation('me');
  const update = useUpdateChart(chart.id);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const relationLabel = t(relationDisplayKey(chart.relation, chart.isSelf, chart.sex));
  const hasName = chart.name.trim() !== '' && (!chart.isSelf || chart.name.trim() !== NO_NAME);
  // Line 1 is the name alone; line 2 starts with the relation: «дочь · 1 мая 2018 · Київ».
  // Self: «Юрий» + «Я · …» when a name is known; plain «Я» + «… · …» otherwise.
  const primary = chart.isSelf && !hasName ? relationLabel : chart.name;
  const secondary = chart.isSelf && !hasName ? null : relationLabel;
  const line2 = [secondary, formatBirthDate(chart.date, i18n.language), chart.placeName].filter(Boolean).join(' · ');

  const showRetry = chart.calcStatus === 'FAILED' || (chart.calcStatus === 'PENDING' && stalled);

  // Once the retry button is gone (the chart went back to PENDING) the next appearance starts enabled.
  useEffect(() => {
    if (!showRetry) setBusy(false);
  }, [showRetry]);

  const retry = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setFailed(false);
    try {
      // Touching a birth field makes the server recompute the chart.
      const saved = await update.mutateAsync({ date: chart.date });
      // Stay disabled: the card re-renders when the new status arrives. The server may answer
      // FAILED right away (queueing failed) — the button never disappears then, so re-enable it.
      if (saved.calcStatus === 'FAILED') setBusy(false);
      onRestartPolling(chart.id);
    } catch {
      setFailed(true);
      setBusy(false);
    }
  };

  let status: JSX.Element | null = null;
  if (chart.calcStatus === 'READY') {
    // A READY row without Big-3 (legacy) has nothing to show.
    if (chart.big3) {
      status = (
        <Big3Strip
          sun={chart.big3.sun}
          moon={chart.big3.moon}
          ascendant={chart.big3.asc}
          data-testid={`chart-big3-${chart.id}`}
        />
      );
    }
  } else if (showRetry) {
    status = <span className={styles.statusFailed}>{t('charts.failed')}</span>;
  } else {
    status = <span className={styles.status}>{t('charts.calculating')}</span>;
  }

  // Stretched link: the button in line 1 opens the chart and its ::after covers the whole card;
  // the retry button sits above that overlay (z-index) so its click never opens the chart.
  return (
    <div className={styles.card} data-testid={`chart-card-${chart.id}`}>
      <button
        type="button"
        className={styles.cardLink}
        onClick={onOpen}
        data-testid={`chart-open-${chart.id}`}
      >
        <span className={styles.name} data-testid={`chart-name-${chart.id}`}>
          {primary}
        </span>
      </button>
      <span className={styles.line2} data-testid={`chart-line2-${chart.id}`}>
        {line2}
      </span>
      {(status || showRetry) && (
        <div className={styles.line3}>
          {status}
          {showRetry && (
            <button
              type="button"
              className={styles.retry}
              onClick={() => void retry()}
              disabled={busy}
              aria-label={t('charts.retryFor', { name: primary })}
              data-testid={`chart-retry-${chart.id}`}
            >
              {t('charts.retry')}
            </button>
          )}
        </div>
      )}
      {failed && (
        <p role="alert" className={styles.retryError} data-testid={`chart-retry-error-${chart.id}`}>
          {t('charts.retryError')}
        </p>
      )}
    </div>
  );
}

export function ChartsList(): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation(['me', 'common']);
  const { charts, limit, othersCount, isLoading, isError, refetch, stalledIds, restartPolling } = useCharts();

  const atCap = othersCount >= limit;
  const self = charts.find((c) => c.isSelf);
  const others = charts.filter((c) => !c.isSelf);
  const goNew = (): void => void navigate('/me/charts/new');

  const addButton = (
    <button
      type="button"
      className={styles.add}
      onClick={goNew}
      disabled={atCap}
      data-testid="charts-add"
    >
      {t('charts.add')}
    </button>
  );

  return (
    <MeSubpageShell title={t('charts.title')} right={addButton} data-testid="charts-page">
      {isLoading && (
        <p className={styles.note} data-testid="charts-loading">
          {t('common:states.loading')}
        </p>
      )}
      {/* A failed background refetch keeps the loaded list; the error shows only when there is nothing. */}
      {isError && charts.length === 0 && (
        <div className={styles.error}>
          <p role="alert" data-testid="charts-error">
            {t('charts.loadError')}
          </p>
          <button type="button" className={styles.retry} onClick={refetch} data-testid="charts-error-retry">
            {t('charts.retry')}
          </button>
        </div>
      )}
      {!isLoading && !(isError && charts.length === 0) && (
        <>
          <div className={styles.list} data-testid="charts-list">
            {self && (
              <ChartCard
                chart={self}
                stalled={stalledIds.has(self.id)}
                onOpen={() => void navigate(`/me/charts/${self.id}`)}
                onRestartPolling={restartPolling}
              />
            )}
            {others.length === 0 && (
              <EmptyState
                title={t('charts.emptyTitle')}
                body={t('charts.emptyBody')}
                glyph="◎"
                ctaLabel={t('charts.emptyCta')}
                onCta={goNew}
                data-testid="charts-empty"
              />
            )}
            {others.map((c) => (
              <ChartCard
                key={c.id}
                chart={c}
                stalled={stalledIds.has(c.id)}
                onOpen={() => void navigate(`/me/charts/${c.id}`)}
                onRestartPolling={restartPolling}
              />
            ))}
          </div>
          <div className={styles.footer}>
            <p className={styles.count} data-testid="charts-count">
              {t('charts.count', { count: othersCount, limit })}
            </p>
            {atCap && (
              <p className={styles.limit} data-testid="charts-limit">
                {t('charts.limitReached')}
              </p>
            )}
          </div>
        </>
      )}
    </MeSubpageShell>
  );
}
